/**
 * Dashboard route configurations for different site types
 * Provides route definitions for wedding and online invitation sites
 */
import { SITE_TYPES } from "../constants";
import { RouteDefinition, SiteData } from "./types";

/**
 * Returns the base URL path for a site's dashboard
 * @param siteId - The unique identifier for the site
 * @returns Base URL path like "/site/123"
 */
export const getBaseUrl = (siteId: number | string): string =>
  `/site/${siteId}`;

/**
 * Site Styles routes unlocked by the new-layouts experience. Each pairs a
 * sidebar with a preview of the home page rather than owning a page of its own.
 *
 * Keyed by route name so the document title comes from here rather than
 * being re-derived from `route.name` at each call site.
 */
export const siteStylesRoutes = {
  layout: "Layout",
  "fonts-and-colors": "Fonts & Colors",
} as const;

export type SiteStylesRouteName = keyof typeof siteStylesRoutes;

export const siteStylesRouteNames: string[] = Object.keys(siteStylesRoutes);

/**
 * Whether a route name is one of the Site Styles routes, narrowing its type.
 *
 * Own properties only — `in` would also match inherited ones like "constructor".
 */
export const isSiteStylesRoute = (
  routeName: string,
): routeName is SiteStylesRouteName =>
  Object.prototype.hasOwnProperty.call(siteStylesRoutes, routeName);

/**
 * Route configuration for wedding sites
 * Full feature set including guest management, pages, designs
 */
const weddingSiteRoutes: RouteDefinition[] = [
  {
    name: "dashboard",
    path: "/dashboard",
  },
  {
    name: "designs",
    path: "/designs?:source",
  },
  {
    name: "settings",
    path: "/settings?:delete",
  },
  // Site Styles views unlocked by the new-layouts experience. Only reachable
  // from the redesigned sidebar.
  {
    name: "layout",
    path: "/layout",
  },
  {
    name: "fonts-and-colors",
    path: "/fonts-and-colors",
  },
  {
    children: [
      {
        name: "all",
        path: "/all",
      },
      {
        name: "rsvp",
        path: "/rsvp?:eventId",
      },
    ],
    name: "guests",
    path: "/guests",
  },
  {
    name: "matching-suite",
    path: "/matching-suite",
  },

  // Pages nested routes
  {
    children: [
      {
        name: "home",
        path: "/home",
      },
      {
        name: "rsvp",
        path: "/rsvp",
      },
      {
        name: "events",
        path: "/events",
      },
      {
        name: "registry",
        path: "/registry",
      },
      {
        name: "photos",
        path: "/photos",
      },
      {
        name: "accommodations",
        path: "/accommodations",
      },
      {
        name: "wedding-party",
        path: "/wedding-party",
      },
      {
        name: "faq",
        path: "/faq",
      },
      {
        name: "custom",
        path: "/:id/:slug",
      },
      {
        name: "edit",
        path: "/edit",
      },
    ],
    name: "pages",
    path: "/pages",
  },
];

/**
 * Route configuration for online invitation sites
 * Includes siteType param in all routes for Qualtrics differentiation
 */
const onlineInvitationSiteRoutes: RouteDefinition[] = [
  // Default params are added to each route so that Qualtrics can
  // differentiate between an online invitation site and a wedding site
  {
    defaultParams: { siteType: "onlineInvitation" },
    name: "settings",
    path: "/settings?:delete?:siteType",
  },
  {
    defaultParams: { siteType: "onlineInvitation" },
    name: "guests",
    path: "/guests?:siteType",
  },
  {
    defaultParams: { siteType: "onlineInvitation" },
    name: "designs",
    path: "/designs?:siteType",
  },
  // Pages nested routes
  {
    children: [
      {
        defaultParams: { siteType: "onlineInvitation" },
        name: "home",
        path: "/home?:siteType",
      },
      {
        defaultParams: { siteType: "onlineInvitation" },
        name: "events",
        path: "/events?:siteType",
      },
      {
        defaultParams: { siteType: "onlineInvitation" },
        name: "registry",
        path: "/registry?:siteType",
      },
      {
        defaultParams: { siteType: "onlineInvitation" },
        name: "custom",
        path: "/:id/:slug?:siteType",
      },
      {
        defaultParams: { siteType: "onlineInvitation" },
        name: "edit",
        path: "/edit?:siteType",
      },
      {
        defaultParams: { siteType: "onlineInvitation" },
        name: "guest-book",
        path: "/guest-book?:siteType",
      },
    ],
    defaultParams: { siteType: "onlineInvitation" },
    name: "pages",
    path: "/sections?:siteType",
  },
];

/**
 * Recursively adds default parameters to routes and their children
 * Used for feature gating and analytics tracking
 *
 * @param routes - Array of route definitions to modify
 * @param newParams - Parameters to add to each route
 * @returns Modified routes with added default parameters
 */
const addDefaultParamsToRoutes = (
  routes: RouteDefinition[],
  newParams: Record<string, any> = {},
): RouteDefinition[] => {
  return routes.map((route) => {
    const modifiedRoute = { ...route };

    // Add defaultParams to current route
    if (modifiedRoute.defaultParams) {
      modifiedRoute.defaultParams = {
        ...modifiedRoute.defaultParams,
        ...newParams,
      };
    } else {
      modifiedRoute.defaultParams = { ...newParams };
    }

    // Add parameters to the path
    if (modifiedRoute.path) {
      const pathParams = Object.keys(newParams)
        .map((key) => key)
        .join("?:");

      if (pathParams) {
        modifiedRoute.path = `${modifiedRoute.path}?:${pathParams}`;
      }
    }

    // Recursively add defaultParams to children
    if (modifiedRoute.children) {
      modifiedRoute.children = addDefaultParamsToRoutes(
        modifiedRoute.children,
        newParams,
      );
    }

    return modifiedRoute;
  });
};

/**
 * Gets the appropriate route configuration based on site data
 * Adds conditional parameters for feature gating (e.g., online invitations sent)
 *
 * @param siteData - Site information including type and invitation count
 * @returns Route configuration array for the site
 */
export const getRoutes = (siteData: SiteData): RouteDefinition[] => {
  const { sentInvitesCount, siteType } = siteData;

  let routes: RouteDefinition[];

  switch (siteType) {
    case SITE_TYPES.WEDDING:
      routes = weddingSiteRoutes;
      break;
    case SITE_TYPES.ONLINE_INVITATION:
      routes = onlineInvitationSiteRoutes;
      break;
    default:
      routes = weddingSiteRoutes;
  }

  // Add onlineInvitationsSent parameter for online invitation sites with more than 5 invites
  if (siteType === SITE_TYPES.ONLINE_INVITATION && sentInvitesCount > 5) {
    routes = addDefaultParamsToRoutes(routes, { onlineInvitationsSent: true });
  }

  return routes;
};

/**
 * Map of site types to their route configurations
 * Useful for static access without site data
 */
export const routesBySiteType: Record<string, RouteDefinition[]> = {
  [SITE_TYPES.WEDDING]: weddingSiteRoutes,
  [SITE_TYPES.ONLINE_INVITATION]: onlineInvitationSiteRoutes,
};

/**
 * Default route name to navigate to for each site type
 */
export const defaultRouteBySiteType: Record<string, string> = {
  [SITE_TYPES.WEDDING]: "dashboard",
  [SITE_TYPES.ONLINE_INVITATION]: "pages",
};
